<template>
  <div
    class="folder-files-item w-full flex items-center border-b-solid border-primary6 overflow-auto"
    :class="{
      'bg-primary5': isSelected,
      '!bg-primary4': isContextMenuOpen,
      'color-text2': isDisabled,
      'cursor-not-allowed': isDisabled,
    }"
  >
    <div class="flex items-center">
      <NCheckbox
        :checked="selectedItems.some((s) => s.id === item.id)"
        class="h-9 w-9 inline-flex items-center justify-center cursor-pointer mx-[5px]"
        :disabled="item.type === 'folder' || isDisabled"
        :value="item.id"
      />
      <div class="my-3">
        <CFileThumbnail v-if="item.type === 'file' && hasValidThumbnail" :item="item as RenderableFile" />
        <div v-else-if="item.type === 'file'" class="w-[152px] h-[100px] rounded-2 overflow-hidden">
          <img
            v-if="defaultThumbnailUrl"
            :alt="item.name || 'File thumbnail'"
            class="w-full h-full object-cover"
            :src="defaultThumbnailUrl"
          />
          <div v-else class="bg-primary6 w-full h-full flex items-center justify-center">
            <CIcon :color="themeVars.primary3" icon="file" size="32px" />
          </div>
        </div>
        <CFileThumbnail v-else :item="item as RenderableFile" />
      </div>
    </div>
    <div class="flex flex-col justify-center gap-1 pa-3 truncate w-full">
      <CTableInput
        :modelValue="nameDescription.name"
        :readonly="true"
        :search="highlightText"
        state="view"
        valueClass="font-700 truncate font-size-[18px]"
      />
      <div
        v-if="isFolderNameDescriptionEnabled && item.type === 'folder' && nameDescription.description"
        class="font-size-[14px] tracking-[0.4px] leading-normal color-text2 whitespace-pre-wrap"
      >
        {{ nameDescription.description }}
      </div>
      <div class="truncate font-size-[12px]" :style="{ color: isContextMenuOpen ? themeVars.text2 : themeVars.text3 }">
        <span v-if="item.type === 'folder'">
          <CIcon class="mr-1" icon="folder" />
          <span v-if="doNotShowFolderDetails">{{ t('canvasUI.common.folder') }}</span>
          <span v-else>{{ getFolderDescription(getTypedItem(item), { hideFilesCount }) }}</span>
        </span>
        <div v-else class="flex gap-2 items-center">
          <div v-if="item.folder?.name" class="flex gap-1 items-center">
            <div>{{ t('canvasUI.files.in') }}</div>
            <div class="color-text3 font-bold hover:underline" @click.stop="emit('open-folder', item.folder)">
              {{
                parseNameOnly(item.folder?.name || '').replace(
                  'root',
                  t('canvasUI.browserApp.content.breadcrumbs.root'),
                )
              }}
            </div>
            <span class="inline-block h-1 w-1 bg-text3 rounded-full"></span>
          </div>
          <component :is="renderContentType(item.content_type)" />
        </div>
      </div>
      <div class="flex flex-wrap gap-2 items-center">
        <CDynamicIndicator
          v-for="metadataProperty of pitcherSettings.metadata_highlight_properties ?? []"
          :key="metadataProperty.key"
          :item="item"
          :metadataProperty="metadataProperty"
        />
      </div>
      <CTableTag
        v-if="item?.tags"
        display-as-text
        :maxTagsToShow="0"
        :modelValue="item?.tags"
        :readonly="true"
        :search="highlightText"
        state="view"
        :tagsClass="'cursor-pointer'"
      />

      <div
        v-if="
          pitcherSettings.enable_metadata_info_in_list_item &&
          item.type === 'file' &&
          Object.keys(metadataForListItem).length > 0
        "
        class="flex flex-wrap gap-2 mb-1"
        :class="{
          'mt-1': item?.tags?.length,
        }"
      >
        <CMetaDataBadge
          v-for="(meta, key) in metadataForListItem"
          :key="key"
          :highlight-text="highlightText"
          :label="meta.label"
          :value="meta.value"
        />
      </div>

      <div class="flex flex-wrap gap-2">
        <template v-if="isAdmin">
          <CContentError v-if="item?.access_type === 'restricted'" only-tag restricted />
          <CInfoBadge
            v-if="isExpired"
            :background-color="themeVars.error2"
            :color="themeVars.error"
            fa-type="fa-kit"
            icon="regular-clock-circle-exclamation"
            :text="t('canvasUI.files.expired')"
          />

          <CInfoBadge
            v-if="isExpiring && !isExpired"
            :background-color="themeVars.warning2"
            :color="themeVars.warning"
            fa-type="fa-kit"
            icon="regular-clock-circle-exclamation"
            :text="`${t('canvasUI.files.expiring')} ${isExpiring}`"
          />
        </template>
        <CInfoBadge
          v-if="item?.permissions?.can_share === false"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          :text="t('canvasUI.files.notShareable')"
        />
        <CPopularityAndEngagementExtended
          v-if="Object.keys(popularityDict)?.length"
          class="flex gap-2 mr-2"
          :engagement="launchDarkly.show_popularity_icons ? popularityDict[data[index].id]?.engagement : undefined"
          :is-recommended="!!recommendedSectionIds.includes(data[index].id)"
          :popularity="launchDarkly.show_popularity_icons ? popularityDict[data[index].id]?.popularity : undefined"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[data[index].id]?.views ?? 0) : 0"
        />
      </div>
    </div>

    <div class="flex justify-end h-full items-center gap-1 cursor-auto py-3 px-4">
      <CButton
        v-if="item.type === 'folder'"
        v-tooltip="t('canvasUI.contentSelectorApp.contextMenu.openFolder')"
        circle
        class="h-9 w-9"
        secondary
        type="default"
        @click.stop="emit('open-folder', item as FolderFile)"
      >
        <template #icon>
          <CIcon class="color-primary" icon="angle-right" size="12" />
        </template>
      </CButton>
      <CButton
        v-if="item.type === 'file'"
        v-tooltip="t('canvasUI.contentSelectorApp.contextMenu.previewFile')"
        circle
        class="h-9 w-9"
        secondary
        type="default"
        @click.stop="emit('preview-file', item, $event)"
      >
        <template #icon>
          <CIcon class="color-primary" icon="eye" size="12" />
        </template>
      </CButton>
      <CButton
        v-if="item.type === 'file'"
        v-tooltip="
          launchDarkly.enable_file_divisible_permission && item.permissions?.can_be_divided === false
            ? t('canvasUI.files.notDivisibleExpanded')
            : t('canvasUI.contentSelectorApp.contextMenu.expandFile')
        "
        circle
        class="h-9 w-9"
        :disabled="isExpandFileDisabled"
        secondary
        type="default"
        @click.stop="emit('expand-file', item as FolderFile)"
      >
        <template #icon>
          <CIcon class="color-primary" icon="grid-2" size="12" />
        </template>
      </CButton>

      <template v-if="isDragNDropEnabled && item?.type !== 'folder'">
        <CButton v-if="isSelected" circle class="h-9 w-9 bg-primary5" secondary type="default">
          <template #icon>
            <CIcon class="ml-[2px] color-primary" icon="grip-dots-vertical" size="12" />
          </template>
        </CButton>

        <CButton
          v-else
          circle
          class="h-9 w-9"
          :class="{
            'bg-base !focus:bg-base !active:bg-base': !isSelected && !isAdded,
            'bg-primary5': isSelected,
            'bg-error2 !focus:bg-error2 !active:bg-error2 ': isAdded,
          }"
          :disabled="isItemDisabled(item as SelectedFile | SelectedFolder | SelectedPage)"
          secondary
          type="default"
          @click.stop="handleItemClick(item as ContentSelectorSelectedItem)"
        >
          <template #icon>
            <CIcon v-if="isAdded" class="ml-[2px] color-error" icon="times" size="12" />
            <CIcon v-else class="ml-[2px] color-primary" icon="plus" size="12" />
          </template>
        </CButton>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { NCheckbox } from 'naive-ui'
import { computed, inject, type ComputedRef, type ToRefs } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CDynamicIndicator from '@lib/components/CDynamicIndicator/CDynamicIndicator.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import type { FolderFile } from '@openapi'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useThemeVars from '@lib/composables/useThemeVars'
import useImageAccessibility from '@lib/composables/useImageAccessibility'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import { getDefaultThumbnailUrl } from '@lib/utils/defaultThumbnail.util'
import type { RenderableFile, ViewCompactFolder } from '@lib/types/files'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type {
  ContentSelectorSelectedItem,
  SelectedFile,
  SelectedFolder,
  SelectedPage,
} from '@content-selector/stores/app'
import type { AppProps } from '@content-selector/appTypes'
import { type OrganizationSettings } from '@lib/types/organizationSettings.types'
import { type InstanceSettings } from '@lib/types/instanceSettings.types'
import { useStore } from '@content-selector/composables/useStore'
import CMetaDataBadge from '@lib/components/CMetaDataBadge/CMetaDataBadge.vue'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const props = withDefaults(
  defineProps<{
    filters: ComputedRef<Record<string, any>>
    highlightText?: string
    isContextMenuOpen?: boolean
    doNotShowFolderDetails?: boolean
    isSelected?: boolean
    index?: number
    item: { id: string; [key: string]: any }
    data: any[]
    selectedItems: (SelectedFile | SelectedFolder)[]
    isDisabled?: boolean
    metadata: Record<string, any>
    hideFilesCount?: boolean
  }>(),
  {
    highlightText: '',
    doNotShowFolderDetails: false,
    index: 0,
    isContextMenuOpen: false,
    isSelected: false,
    metadata: () => ({}),
  },
)

const emit = defineEmits<{
  (e: 'preview-file', item: { id: string; [key: string]: any }, event: MouseEvent): void
  (e: 'expand-file', item: FolderFile): void
  (e: 'open-folder', item: FolderFile): void
}>()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { isAdmin, isDragNDropEnabled, addedItems, handleItemClick, isItemDisabled } = useStore()
const { createNameDescriptionForItem, parseNameOnly, isFolderNameDescriptionEnabled } = useFolderNameDescription()

const isAdded = computed(() => addedItems.value.find((item) => item.id === props.item.id))

const nameDescription = createNameDescriptionForItem(
  computed(() => props.item.name || ''),
  computed(() => props.item.type || ''),
)

const expiresAt = computed(() => {
  return props.item.expires_at || props.item.file?.expires_at
})

const isExpired = computed(() => {
  return isFileExpired(expiresAt.value)
})

const isExpiring = computed(() => {
  return isFileExpiring(expiresAt.value)
})

const metadataForListItem = computed(() => {
  const skipKeys = pitcherSettings.value?.skip_metadata_keys_in_list_item || []
  return Object.fromEntries(Object.entries(props.metadata).filter(([key]) => !skipKeys.includes(key)))
})

const appProps = inject<ToRefs<AppProps>>('appProps')
const { t } = useI18n()
const themeVars = useThemeVars()
const { popularityDict } = useSectionPopularity(props.filters)
const { recommendedSectionIds } = useRecommendations()
const { getFolderDescription, renderContentType, isFileExpired, isFileExpiring } = useFileDisplayHelpers()
const getTypedItem = (item: any) => item as ViewCompactFolder

const thumbnailUrl = computed(() => {
  if (props.item.type === 'file') {
    return props.item.thumbnail_url || null
  }
  return null
})

const defaultThumbnailUrl = computed(() => {
  if (props.item.type === 'file') {
    return getDefaultThumbnailUrl(props.item as any)
  }
  return null
})

const { hasValidThumbnail } = useImageAccessibility({
  thumbnailUrl,
  hasAIEnabled: computed(() => false), // Not used here
  hasFile: computed(() => props.item.type === 'file'),
})

const isExpandFileDisabled = computed(() => {
  return (
    props.isDisabled ||
    appProps?.contentType.value === 'file' ||
    props.item.content_type !== 'pdf' ||
    (launchDarkly.value.enable_file_divisible_permission && props.item.permissions?.can_be_divided === false)
  )
})
</script>
